/* Supernova landing page controller.
   Replaces the Claude Design React runtime (support.js) with a small vanilla script.
   Behaviour mirrors the `Component extends DCLogic` class in the export:
   - picks the active scene from scroll position and drives <supernova-stage>
   - reports the chip slot geometry so the 3D chip is framed into the DOM slot
   - on mobile, holds the text cards for REVEAL_DELAY_MS after a scene change
   - in the "round" scene, alternates 1/40 speed (one round) with a short real-time burst
   - header mega-menus: open on hover/focus of a trigger, close on header leave / Escape / focus leaving the header */
(() => {
  'use strict';
  const ROUND_MS = 600;            // export prop default: roundMs
  const REVEAL_DELAY_MS = 1600;    // export prop default: revealDelayMs
  const DUR = ROUND_MS / 1000;
  const ORDER = ['hero', 'slow', 'shards', 'round', 'speed', 'computer', 'egld', 'builders', 'close'];
  const NAMES = { hero: 'Supernova', slow: 'Before', speed: 'Cross-shard', globe: 'Global consensus', round: 'Inside the round', shards: 'The decoupling', computer: 'Under load', egld: 'The path', builders: 'For builders', close: 'Final' };

  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));

  const stage = $('supernova-stage');
  const grid = $('validator-grid');
  const sceneIndexEl = $('[data-bind="sceneIndex"]');
  const sceneNameEl = $('[data-bind="sceneName"]');
  const slowLabelEls = $$('[data-bind="slowLabel"]');
  const slowDurEls = $$('[data-slow-dur]');        // animation-duration = DUR * slow
  const slowDelayEls = $$('[data-slow-delay]');    // animation-delay = fraction * DUR * slow
  const cards = $$('[data-card]');
  const scenes = $$('[data-scene]');

  const state = { scene: 'hero', chipFrame: '', revealed: true, slow: 40 };
  let revealTimer = 0, slowTimer = 0, pending = false, lastScene = null;

  const setAttr = (el, name, value) => { if (el && el.getAttribute(name) !== value) el.setAttribute(name, value); };
  const setText = (el, value) => { if (el && el.textContent !== value) el.textContent = value; };

  function render() {
    const { scene, slow, revealed } = state;
    setAttr(stage, 'scene', scene);
    setAttr(stage, 'chip-frame', state.chipFrame);
    setAttr(stage, 'time-scale', scene === 'round' ? String(1 / slow) : '');
    setAttr(grid, 'slow', String(slow));
    setText(sceneIndexEl, String(ORDER.indexOf(scene) + 1).padStart(2, '0'));
    setText(sceneNameEl, NAMES[scene] || '');
    const slowLabel = slow > 1 ? '×40 slower' : 'real time';
    slowLabelEls.forEach(el => setText(el, slowLabel));
    const slowDur = (DUR * slow) + 's';
    slowDurEls.forEach(el => { if (el.style.animationDuration !== slowDur) el.style.animationDuration = slowDur; });
    slowDelayEls.forEach(el => {
      const d = (parseFloat(el.dataset.slowDelay) * DUR * slow) + 's';
      if (el.style.animationDelay !== d) el.style.animationDelay = d;
    });
    const op = revealed ? '1' : '0', tf = revealed ? 'none' : 'translateY(28px)';
    cards.forEach(el => { if (el.style.opacity !== op) el.style.opacity = op; if (el.style.transform !== tf) el.style.transform = tf; });
    if (lastScene !== scene) { lastScene = scene; cycleSlow(scene === 'round'); }
  }

  // inside-the-round scene: one full round at 1/40 speed, then a short burst at real time, repeating
  function cycleSlow(on) {
    clearTimeout(slowTimer);
    if (!on) { if (state.slow !== 40) { state.slow = 40; render(); } return; }
    const slowLeg = () => { state.slow = 40; render(); slowTimer = setTimeout(fastLeg, ROUND_MS * 40); };
    const fastLeg = () => { state.slow = 1; render(); slowTimer = setTimeout(slowLeg, ROUND_MS * 6); };
    slowLeg();
  }

  function pickScene() {
    const mid = innerHeight * .55;
    let best = null, bd = Infinity;
    for (const el of scenes) {
      const r = el.getBoundingClientRect(), c = r.top + r.height / 2, d = Math.abs(c - mid);
      if (d < bd) { bd = d; best = el.dataset.scene; }
    }
    // report where the chip slot of the active chip scene sits, so the camera frames the chip into it
    let frame = state.chipFrame || '';
    const slot = best && $(`[data-scene="${best}"] [data-chip-slot]`);
    if (slot) {
      const r = slot.getBoundingClientRect(), pad = .92;
      const size = Math.min(r.height, r.width) * pad;
      frame = ((r.top + r.height / 2) / innerHeight).toFixed(3) + ',' + (size / innerHeight).toFixed(3);
    }
    if (best && best !== state.scene) {
      clearTimeout(revealTimer);
      // mobile only: the cards cover the world there, so let it play first
      const mobile = matchMedia('(max-width: 720px)').matches;
      const hold = !mobile || best === 'hero' || best === 'close' ? 0 : REVEAL_DELAY_MS;
      state.scene = best; state.chipFrame = frame; state.revealed = hold === 0;
      render();
      if (hold) revealTimer = setTimeout(() => { state.revealed = true; render(); }, hold);
    } else if (frame !== state.chipFrame) {
      state.chipFrame = frame;
      render();
    }
  }

  const onScroll = () => { if (pending) return; pending = true; requestAnimationFrame(() => { pending = false; pickScene(); }); };
  addEventListener('scroll', onScroll, { passive: true });
  addEventListener('resize', onScroll);
  render();
  pickScene();

  // ---- header mega-menus (export: onMouseEnter="{{ m.open }}" per item, onMouseLeave="{{ closeMenu }}" on the header)
  const header = $('header');
  const panels = $$('[data-menu]');
  const triggers = $$('[data-menu-trigger]');
  if (header && panels.length) {
    let open = null;
    const setMenu = (label) => {
      label = label || null;
      if (open === label) return;
      open = label;
      panels.forEach(p => { p.hidden = p.dataset.menu !== label; });
      triggers.forEach(t => { const a = $('[aria-expanded]', t); if (a) a.setAttribute('aria-expanded', String(t.dataset.menuTrigger === label)); });
    };
    triggers.forEach(t => {
      t.addEventListener('mouseenter', () => setMenu(t.dataset.menuTrigger));
      t.addEventListener('focusin', () => setMenu(t.dataset.menuTrigger));
      // top-level items of a menu group are hover targets only (href="#" in the export): do not jump to the top of the page
      const a = $('a[href="#"]', t);
      if (a) a.addEventListener('click', e => { e.preventDefault(); setMenu(t.dataset.menuTrigger); });
    });
    $$('[data-menu-close]').forEach(el => el.addEventListener('mouseleave', () => setMenu(null)));
    header.addEventListener('focusout', e => { if (!header.contains(e.relatedTarget)) setMenu(null); });
    addEventListener('keydown', e => { if (e.key === 'Escape') setMenu(null); });
  }
})();
